import React, { useEffect, useRef, lazy, Suspense } from "react";
import AADWrapper from "./components/Security/AADWrapper";
import { I18n } from "./I18n";
import { RouteTelemetry, TelemetryUserContext, trackAppLoaded } from "./otel";
import { ConfigProvider } from "./store/context";
import { useSecuritySetting, useConfigStore } from "./store/store";
import LoadingScreen from "./components/LoadingScreen/LoadingScreen";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "./theme";
import { AppInsightsContext, AppInsightsErrorBoundary, ReactPlugin } from "@microsoft/applicationinsights-react-js";
import ErrorPage from "./components/ErrorPage/ErrorPage";
import {
    Route,
    BrowserRouter as Router,
    Routes
} from "react-router-dom";
import { AlertWindow } from "./components/Alert/AlertWindow";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

const OnePage = lazy(() => import("./pages/OnePage"));
const AdminSPA = lazy(() => import("./pages/AdminSPA"));
const queryClient = new QueryClient();

const ErrorFallback: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
    <div>
        <h2>An error occurred</h2>
        <button onClick={onRetry}>Retry</button>
    </div>
);

interface ErrorBoundaryWrapperProps {
    reactPlugin: ReactPlugin;
    children: React.ReactElement;
}

const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({ reactPlugin, children }) => {
    const handleError = () => <ErrorFallback onRetry={() => window.location.reload()} />;

    return (
        <AppInsightsContext.Provider value={reactPlugin}>
            <AppInsightsErrorBoundary appInsights={reactPlugin} onError={handleError}>
                {children}
            </AppInsightsErrorBoundary>
        </AppInsightsContext.Provider>
    );
};

const App: React.FC = () => {
    const { initialized, authRequired, scopes, validateMsalSession } = useSecuritySetting();
    const { reactPlugin, appInsights, initializationFailed, init, translations } = useConfigStore();
    const isCheckingSessionRef = useRef(false);
    const hasTrackedAppLoadRef = useRef(false);

    useEffect(() => {
        if (!appInsights || hasTrackedAppLoadRef.current) {
            return;
        }

        hasTrackedAppLoadRef.current = true;
        trackAppLoaded(appInsights, authRequired);
    }, [appInsights, authRequired]);

    useEffect(() => {
        if (!initialized) {
            init();
        }
    }, [init, initialized]);

    useEffect(() => {
        const handleFocus = async () => {
            if (!authRequired || isCheckingSessionRef.current) {
                return;
            }

            isCheckingSessionRef.current = true;
            try {
                const isSessionValid = await validateMsalSession();
                if (!isSessionValid) {
                    console.warn("MSAL session is no longer valid. Reloading page.");
                    window.location.reload();
                }
            } finally {
                isCheckingSessionRef.current = false;
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                void handleFocus();
            }
        };

        const handleWindowFocus = () => {
            void handleFocus();
        };

        window.addEventListener("focus", handleWindowFocus);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener("focus", handleWindowFocus);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [authRequired, validateMsalSession]);

    if (initializationFailed) {
        return <ErrorPage message="Failed to load configuration, try reloading the page." />;
    }

    if (!initialized || !appInsights || !reactPlugin) {
        return <LoadingScreen text="Loading the configuration from backend..." />;
    }

    const appJSX = (
        <ConfigProvider>
            <QueryClientProvider client={queryClient}>
                <I18n>
                    {translations && Object.keys(translations).length > 0 ? (
                        <>
                            <AlertWindow />
                            <ThemeProvider theme={theme}>
                                <Router>
                                    <RouteTelemetry appInsights={appInsights} />
                                    <Suspense fallback={<LoadingScreen text="Loading..." />}>
                                        <Routes>
                                            <Route path="/" element={
                                                <OnePage />
                                            } />
                                            <Route path="/admin/*" element={<AdminSPA />} />
                                        </Routes>
                                    </Suspense>
                                </Router>
                            </ThemeProvider>
                        </>
                    ) : (
                        <LoadingScreen text="Loading translations..." />
                    )}
                </I18n>
            </QueryClientProvider>
        </ConfigProvider>
    );
    return authRequired ? (
        <ErrorBoundaryWrapper reactPlugin={reactPlugin}>
            <AADWrapper scopes={scopes}>
                <TelemetryUserContext appInsights={appInsights} />
                {appJSX}
            </AADWrapper>
        </ErrorBoundaryWrapper>
    ) : (
        appJSX
    );
};

export default App;
